import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Headset, Loader2, UserMinus, UserPlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { adminFetch } from '@/hooks/admin/useAdminApi';

/**
 * Phone desk logins — who can use /desk (the Smith.ai live receptionists).
 * Superadmin only (admin-set-phone-desk re-checks). The same flag is the
 * "Phone desk" switch on the gateway's People & Access console
 * (admin-staff set_phone_desk). This only grants the flag: the login must
 * already exist.
 */

interface DeskUserRow {
  id: string;
  full_name: string;
  email: string | null;
  created_at: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function PhoneDeskPanel() {
  const qc = useQueryClient();
  const { data: users, isLoading } = useQuery<DeskUserRow[]>({
    queryKey: ['admin-phone-desk'],
    queryFn: async () => (await adminFetch<{ desk_users: DeskUserRow[] }>('admin-set-phone-desk')).desk_users ?? [],
    staleTime: 60 * 1000,
  });
  const setFlag = useMutation<unknown, Error, { email: string; enable: boolean; name?: string }>({
    mutationFn: (input) => adminFetch('admin-set-phone-desk', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-phone-desk'] }),
  });
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  async function grant() {
    setMessage(null);
    try {
      await setFlag.mutateAsync({ email: email.trim(), enable: true, name: name.trim() || undefined });
      setMessage(`${email.trim()} can now use the phone desk at /desk.`);
      setEmail('');
      setName('');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    }
  }
  async function revoke(addr: string | null) {
    if (!addr) return;
    if (!window.confirm(`Turn off phone desk access for ${addr}?`)) return;
    setMessage(null);
    try {
      await setFlag.mutateAsync({ email: addr, enable: false });
      setMessage(`Phone desk access removed for ${addr}.`);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface shadow-card">
      <div className="flex flex-wrap items-center gap-2 border-b border-brix-border px-4 py-3">
        <Headset size={15} className="text-brix-primary" aria-hidden />
        <h2 className="font-display text-sm font-bold text-brix-ink">Phone desk logins</h2>
        <span className="text-xs text-brix-muted">
          live receptionists — they see /desk only: look up, orders, service requests
        </span>
      </div>
      <div className="flex flex-col gap-2 border-b border-brix-border px-4 py-3 sm:flex-row">
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="receptionist@example.com" type="email" className="sm:max-w-xs" />
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className="sm:max-w-[180px]" />
        <Button disabled={!EMAIL_RE.test(email.trim()) || setFlag.isPending} onClick={() => void grant()}>
          {setFlag.isPending ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} aria-hidden />}
          Turn on phone desk
        </Button>
      </div>
      {message ? <div className="px-4 py-2 text-xs text-brix-muted">{message}</div> : null}
      {isLoading ? (
        <div className="flex items-center gap-2 px-4 py-4 text-sm text-brix-muted">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : (users ?? []).length === 0 ? (
        <div className="px-4 py-4 text-sm text-brix-muted">
          No phone desk logins yet. Create the login first (gateway People &amp; Access), then turn it on here.
        </div>
      ) : (
        (users ?? []).map((u) => (
          <div key={u.id} className="flex items-center justify-between border-b border-brix-border px-4 py-2.5 last:border-b-0">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-brix-ink">{u.full_name}</div>
              <div className="truncate text-xs text-brix-muted">{u.email ?? u.id}</div>
            </div>
            <Button size="sm" variant="subtle" disabled={setFlag.isPending || !u.email} onClick={() => void revoke(u.email)}>
              <UserMinus size={13} aria-hidden /> Turn off
            </Button>
          </div>
        ))
      )}
    </div>
  );
}
